import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { AppModal, ConfirmModal } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import filtersStyles from "../SignRequestsFilters.styles";
import {
  DEFAULT_TAG_COLOR,
  SIGN_REQUESTS_TAG_COLOR_KEYS,
  signRequestsTagColors,
  SignRequestsTagColor,
} from "../SignRequestsFilters.tokens";
import { SignRequestTag } from "../SignRequestsFiltersHelper";
import {
  LockGlyph,
  PencilGlyph,
  PlusGlyph,
  TrashGlyph,
} from "./SignRequestsIcons";

const styles = filtersStyles;

/** Marks the editor as creating a tag rather than editing an existing one. */
const NEW_TAG_ID = "new";

interface TagDraft {
  id: string;
  name: string;
  color: SignRequestsTagColor;
}

interface ManageTagsDialogProps {
  open: boolean;
  tags: SignRequestTag[];
  /** Open straight into the new-tag editor ("Create your first tag"). */
  startWithNewTag?: boolean;
  /** The tag list is being read. */
  loading?: boolean;
  onClose: () => void;
  /** Each resolves `true` once saved; `false` keeps the editor open to retry. */
  onCreate: (name: string, color: SignRequestsTagColor) => Promise<boolean>;
  onUpdate: (
    id: string,
    name: string,
    color: SignRequestsTagColor,
  ) => Promise<boolean>;
  onDelete: (tag: SignRequestTag) => Promise<boolean>;
}

const getUsageLabel = (count?: number) => {
  if (count === undefined) return "";
  if (!count) return "Not used yet";
  return `Used on ${count} ${count === 1 ? "request" : "requests"}`;
};

/**
 * The account's tags: anyone can add one, while renaming, recolouring and
 * deleting follow each tag's own `canManage` — the backend's `tag.manage`
 * check — so a tag this user cannot manage simply has no edit controls.
 */
const ManageTagsDialog = ({
  open,
  tags,
  startWithNewTag = false,
  loading = false,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}: ManageTagsDialogProps) => {
  const [draft, setDraft] = useState<TagDraft | null>(null);
  const [deleting, setDeleting] = useState<SignRequestTag | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeleting(null);
    setIsSaving(false);
    setDraft(
      startWithNewTag
        ? { id: NEW_TAG_ID, name: "", color: DEFAULT_TAG_COLOR }
        : null,
    );
  }, [open, startWithNewTag]);

  const trimmed = draft?.name.trim() ?? "";
  const isReadOnly = tags.length > 0 && !tags.some((tag) => tag.canManage);

  const saveDraft = async () => {
    if (!draft || !trimmed || isSaving) return;
    setIsSaving(true);
    const saved =
      draft.id === NEW_TAG_ID
        ? await onCreate(trimmed, draft.color)
        : await onUpdate(draft.id, trimmed, draft.color);
    setIsSaving(false);
    if (saved) setDraft(null);
  };

  const confirmDelete = async () => {
    if (!deleting || isSaving) return;
    setIsSaving(true);
    await onDelete(deleting);
    setIsSaving(false);
    setDeleting(null);
  };

  const renderEditor = (submitLabel: string) =>
    draft && (
      <Box
        component="form"
        sx={styles.tagEditor}
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          saveDraft();
        }}
      >
        <CustomInput
          autoFocus
          name="tagName"
          placeHolder="Tag name"
          value={draft.name}
          propsToInputElement={{ "aria-label": "Tag name", maxLength: 50 }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft({ ...draft, name: event.target.value })
          }
        />
        <Box role="radiogroup" aria-label="Tag color" sx={styles.swatchRow}>
          {SIGN_REQUESTS_TAG_COLOR_KEYS.map((color) => (
            <ButtonBase
              key={color}
              role="radio"
              aria-checked={draft.color === color}
              aria-label={signRequestsTagColors[color].label}
              sx={styles.swatch(color, draft.color === color)}
              onClick={() => setDraft({ ...draft, color })}
            >
              <span />
            </ButtonBase>
          ))}
        </Box>
        <Box sx={styles.editorActions}>
          <Button
            size="small"
            variant="outlined"
            disabled={isSaving}
            onClick={() => setDraft(null)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            type="submit"
            disabled={!trimmed || isSaving}
            startIcon={
              isSaving ? (
                <CircularProgress size={13} thickness={5} color="inherit" />
              ) : undefined
            }
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    );

  const deletingCount = deleting?.usageCount;

  return (
    <>
      <AppModal
        open={open && !deleting}
        onClose={onClose}
        title="Manage tags"
        size="md"
        hideIcon
        hideFooter
        busy={isSaving}
        headerAction={
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlusGlyph />}
            onClick={() =>
              setDraft({ id: NEW_TAG_ID, name: "", color: DEFAULT_TAG_COLOR })
            }
          >
            New tag
          </Button>
        }
      >
        {isReadOnly && (
          <Box sx={styles.dialogNotice}>
            <LockGlyph sx={styles.dialogNoticeGlyph} />
            Only admins can edit or delete tags.
          </Box>
        )}
        {draft?.id === NEW_TAG_ID && renderEditor("Create tag")}
        {tags.length === 0 && loading && (
          <Box sx={styles.dialogLoading}>
            <CircularProgress
              size={24}
              role="status"
              aria-label="Loading tags"
            />
          </Box>
        )}
        {tags.length === 0 && !loading && draft?.id !== NEW_TAG_ID && (
          <Box sx={styles.dialogEmpty}>
            Tags help you group requests. Create your first tag.
          </Box>
        )}
        {tags.map((tag) =>
          draft?.id === tag.id ? (
            <Box key={tag.id}>{renderEditor("Save")}</Box>
          ) : (
            <Box key={tag.id} sx={styles.dialogRow}>
              <Box component="span" sx={styles.tagDot(tag.color, true)} />
              <Box component="span" sx={styles.dialogRowName}>
                {tag.name}
              </Box>
              <Box component="span" sx={styles.dialogRowMeta}>
                {getUsageLabel(tag.usageCount)}
              </Box>
              {tag.canManage && (
                <>
                  <IconButton
                    aria-label={`Edit ${tag.name}`}
                    onClick={() =>
                      setDraft({
                        id: tag.id,
                        name: tag.name,
                        color: tag.color,
                      })
                    }
                    sx={styles.dialogAction}
                  >
                    <PencilGlyph sx={styles.dialogActionGlyph} />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${tag.name}`}
                    onClick={() => {
                      setDraft(null);
                      setDeleting(tag);
                    }}
                    sx={styles.dialogDangerAction}
                  >
                    <TrashGlyph sx={styles.dialogActionGlyph} />
                  </IconButton>
                </>
              )}
            </Box>
          ),
        )}
      </AppModal>

      <ConfirmModal
        open={open && Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={`Delete "${deleting?.name ?? ""}"?`}
        message={
          deletingCount
            ? `It will be removed from ${deletingCount} ${
                deletingCount === 1 ? "sign request" : "sign requests"
              }.`
            : "It isn't used on any sign requests."
        }
        confirmLabel="Delete tag"
        cancelLabel="Cancel"
        tone="destructive"
        loading={isSaving}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default ManageTagsDialog;
